import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { SUPPORT_CONTACT, API_ENDPOINTS } from '@caro/shared/constants';
import { BuildingLogoChip } from '@caro/shared/components';
import { Z_INDEX } from '../constants/zIndex';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, logout, isLoading, error, api, refreshToken } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [revokingOthers, setRevokingOthers] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await login(email, password);
      if (result.activeDeviceCount > 1) {
        // Other devices are logged in — show dialog
        setShowSessionDialog(true);
      }
      // If activeDeviceCount <= 1, useAuth sets isAuthenticated=true
      // and App.tsx will redirect to home automatically
    } catch {
      // Error is handled by useAuth
    }
  };

  const handleLogoutOthers = async () => {
    setRevokingOthers(true);
    try {
      await api.post(API_ENDPOINTS.AUTH_DEVICES_REVOKE_OTHERS, {
        currentRefreshToken: refreshToken,
      });
    } catch {
      // Even if revoke fails, let the user proceed — they're already logged in
    }
    setRevokingOthers(false);
    setShowSessionDialog(false);
    // isAuthenticated is already true, App.tsx will redirect to home
  };

  const handleCancelLogin = async () => {
    setShowSessionDialog(false);
    await logout();
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '24px',
      backgroundColor: 'var(--caro-bg)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        backgroundColor: 'var(--caro-card-bg)',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: 'var(--caro-card-shadow, 0 4px 24px rgba(0,0,0,0.08))',
      }}>
        {/* Building logo (Phase 2 / T2.5). Pre-auth, so no API branding yet —
         *  chip renders `null` when no logo URL is available, keeping the
         *  layout unchanged for the unbranded case. */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <BuildingLogoChip maxHeight={56} />
        </div>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: 'var(--font-heading)', color: 'var(--caro-primary)', marginBottom: '8px' }}>
            {t.login.appName}
          </h1>
          <p style={{ color: 'var(--caro-text-light)', fontSize: 'var(--font-body)' }}>
            {t.login.subtitle}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
              {t.login.emailLabel}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: 'var(--font-body)',
                borderRadius: '8px',
                border: '2px solid var(--caro-border)',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>
              {t.login.passwordLabel}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: 'var(--font-body)',
                borderRadius: '8px',
                border: '2px solid var(--caro-border)',
                outline: 'none',
              }}
            />
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#FEF2F2',
              color: '#DC2626',
              borderRadius: '8px',
              fontSize: 'var(--font-body)',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: 'var(--font-body)',
              fontWeight: 600,
              backgroundColor: 'var(--caro-primary)',
              color: 'var(--caro-on-primary, white)',
              opacity: isLoading ? 0.7 : 1,
              border: 'none',
              borderRadius: '8px',
            }}
          >
            {isLoading ? t.login.signingIn : t.login.signIn}
          </button>
        </form>

        {/* Divider */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          margin: '24px 0',
          color: 'var(--caro-text-light)',
          fontSize: 'var(--font-body)',
        }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--caro-border)' }} />
          {t.common.or}
          <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--caro-border)' }} />
        </div>

        {/* QR Code Login */}
        <button
          onClick={() => navigate('/qr-login')}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: 'var(--font-body)',
            fontWeight: 600,
            backgroundColor: 'var(--caro-bg)',
            color: 'var(--caro-text)',
            border: '2px solid var(--caro-border)',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          {t.login.qrButton}
        </button>

        <p style={{
          textAlign: 'center',
          marginTop: '24px',
          fontSize: 'var(--font-body)',
          color: 'var(--caro-text-light)',
        }}>
          {t.login.helpText} {SUPPORT_CONTACT.email}
        </p>
      </div>
      {/* Admin Setup Link */}
      <button
        onClick={() => navigate('/admin-setup')}
        style={{
          marginTop: '24px',
          background: 'none',
          border: 'none',
          color: 'var(--caro-text-light)',
          fontSize: 'var(--font-body)',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {t.login.adminSetup}
      </button>
      {/* Session Conflict Dialog */}
      {showSessionDialog && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: Z_INDEX.BOOT,
          padding: '24px',
        }}>
          <div style={{
            backgroundColor: 'var(--caro-card-bg, #fff)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <div style={{
              fontSize: 'var(--font-heading)',
              textAlign: 'center',
              marginBottom: '16px',
            }}>
              {'\u26A0\uFE0F'}
            </div>
            <h2 style={{
              fontSize: 'var(--font-body)',
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: '12px',
              color: 'var(--caro-text, #1a1a1a)',
            }}>
              {t.login.activeSessionTitle}
            </h2>
            <p style={{
              fontSize: 'var(--font-body)',
              textAlign: 'center',
              color: 'var(--caro-text-light, #666)',
              marginBottom: '28px',
              lineHeight: 1.5,
            }}>
              {t.login.activeSessionMessage}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={handleLogoutOthers}
                disabled={revokingOthers}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: 'var(--font-body)',
                  fontWeight: 600,
                  backgroundColor: '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: revokingOthers ? 'wait' : 'pointer',
                  opacity: revokingOthers ? 0.7 : 1,
                }}
              >
                {t.login.logoutOthers}
              </button>
              <button
                onClick={handleCancelLogin}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: 'var(--font-body)',
                  fontWeight: 600,
                  backgroundColor: 'var(--caro-bg, #f5f5f5)',
                  color: 'var(--caro-text, #1a1a1a)',
                  border: '2px solid var(--caro-border, #e0e0e0)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {t.login.cancelLogin}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
